import React, { useRef } from 'react';
import { Camera, X, FileText } from 'lucide-react';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_FILE_SIZE_LABEL = '5MB';

export default function BillUpload({ files, onChange }) {
  const inputRef = useRef(null);

  const handleFiles = (e) => {
    const selected = Array.from(e.target.files || []);

    for (const file of selected) {
      if (file.size > MAX_FILE_SIZE) {
        alert(`File "${file.name}" exceeds ${MAX_FILE_SIZE_LABEL} limit`);
        continue;
      }
      const reader = new FileReader();
      reader.onload = () => {
        onChange(prev => [...prev, {
          filename: file.name,
          originalName: file.name,
          mimetype: file.type,
          size: file.size,
          data: reader.result, // base64 data URL
          preview: file.type.startsWith('image/') ? reader.result : null
        }]);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = ''; // Reset input
  };

  const removeFile = (index) => {
    onChange(prev => prev.filter((_, i) => i !== index));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  return (
    <div data-testid="bill-upload-section">
      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
        Bill / Receipt Upload
      </p>

      {/* Upload area */}
      <div
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-slate-300 rounded-md p-4 text-center cursor-pointer
          hover:border-slate-400 hover:bg-slate-50 transition-colors active:scale-[0.99]"
        data-testid="bill-upload-area"
      >
        <Camera className="w-6 h-6 mx-auto text-slate-400 mb-1" />
        <p className="text-xs text-slate-600 font-medium">Tap to upload or take photo</p>
        <p className="text-[10px] text-slate-400 mt-0.5">Images or PDF, max {MAX_FILE_SIZE_LABEL} each</p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        capture="environment"
        multiple
        onChange={handleFiles}
        className="hidden"
        data-testid="bill-file-input"
      />

      {/* File list */}
      {files.length > 0 && (
        <div className="mt-2 space-y-1">
          {files.map((file, i) => (
            <div
              key={file.filename + '-' + file.size}
              className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md p-1.5"
              data-testid={`bill-item-${file.filename}`}
            >
              {file.preview ? (
                <img src={file.preview} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded bg-slate-200 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-slate-500" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-medium text-slate-700 truncate">{file.originalName || file.filename}</p>
                <p className="text-[10px] text-slate-400">{formatSize(file.size)}</p>
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                className="p-0.5 hover:bg-red-50 rounded"
                data-testid={`remove-bill-${i}`}
              >
                <X className="w-3.5 h-3.5 text-red-500" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
